'use client'

import { ChevronDownIcon } from 'primitives/icons'
import * as Accordion from 'primitives/accordion'
import { Highlight, themes } from 'prism-react-renderer'
import * as React from 'react'
import { toast } from 'react-hot-toast'

import { CodeIcon } from './code-icon'

const accordionIcon = (
  <ChevronDownIcon
    className="h-[24px] w-[24px] text-gray-500 transition-transform group-data-[state='open']/trigger:rotate-180"
    aria-hidden
  />
)

function CopyButton({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      toast.success('Code copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
      // oxlint-disable-next-line unicorn/catch-error-name
    } catch (err) {
      toast.error('Failed to copy code')
    }
  }

  return (
    // oxlint-disable-next-line react/button-has-type
    <button
      onClick={handleCopy}
      className="absolute right-2 top-2 rounded px-2 py-1 text-12 text-cf-grey-300 hover:text-cf-grey-100"
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  )
}

export function CodeExample({
  code,
  language = 'tsx',
  lightLink = false,
}: {
  code: string
  language?: string
  lightLink?: boolean
}) {
  const colorViewCodeLink = lightLink
    ? 'text-cf-grey-300 hover:text-cf-grey-100 active:text-cf-grey-100 focus:text-cf-grey-100'
    : 'text-cf-grey-700 hover:text-cf-grey-900 active:text-cf-grey-900 focus:text-cf-grey-900'
  return (
    <Accordion.Root type="single" collapsible className="mt-4">
      <Accordion.Item value="code-example">
        <Accordion.Header>
          <Accordion.Trigger
            className={`text-12 flex w-full items-center justify-between font-semibold underline ${colorViewCodeLink}`}
            icon={accordionIcon}
          >
            <div className="flex items-center gap-1 rounded border border-cf-grey-400 text-cf-grey-400 px-1 py-1 hover:border-cf-grey-500">
              <CodeIcon className="h-3 w-3" aria-hidden />
            </div>
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content className="mt-4">
          <div className="bg-cf-grey-1100 overflow-x-auto rounded-md relative px-8">
            <CopyButton code={code} />
            <Highlight theme={themes.nightOwl} code={code} language={language}>
              {({ className, style, tokens, getLineProps, getTokenProps }) => (
                <pre className={`${className} pt-10 pb-4`} style={style}>
                  {tokens.map((line, i) => (
                    <div key={i} {...getLineProps({ line })}>
                      {line.map((token, key) => (
                        <span key={key} {...getTokenProps({ token })} />
                      ))}
                    </div>
                  ))}
                </pre>
              )}
            </Highlight>
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  )
}
